/** Master-PIN Seiten-Sperre — Session + Hash-Helfer (kein Klartext). */

export type PinLockConfig = {
  isPinActive: boolean;
  pinHash: string;
  lockedPageIds: string[];
  updatedAt?: string;
};

/** Dauerhaft frei — nie sperrbar, nie in der Konfigurationsliste. */
export const PIN_LOCK_NEVER_LOCK_PAGES = ['startseite', 'jahresuebersicht'] as const;

export const EMPTY_PIN_LOCK_CONFIG: PinLockConfig = {
  isPinActive: false,
  pinHash: '',
  lockedPageIds: [],
};

export const PIN_SESSION_EVENT = 'pe-pin-session-changed';

const sessionKey = (uid: string) => `pe_pin_session_${uid}`;

export function normalizePinLockConfig(raw: unknown): PinLockConfig {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_PIN_LOCK_CONFIG };
  const o = raw as Record<string, unknown>;
  const locked = Array.isArray(o.lockedPageIds)
    ? o.lockedPageIds.filter(
        (id): id is string =>
          typeof id === 'string' &&
          id.length > 0 &&
          !(PIN_LOCK_NEVER_LOCK_PAGES as readonly string[]).includes(id)
      )
    : [];
  return {
    isPinActive: o.isPinActive === true,
    pinHash: typeof o.pinHash === 'string' ? o.pinHash : '',
    lockedPageIds: locked,
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : undefined,
  };
}

/** Session-Entsperrung gilt bis Schließen der App/des Tabs (sessionStorage). */
export function isPinSessionUnlocked(uid: string): boolean {
  if (!uid || typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(sessionKey(uid)) === '1';
}

export function setPinSessionUnlocked(uid: string): void {
  if (!uid || typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(sessionKey(uid), '1');
  try {
    window.dispatchEvent(new Event(PIN_SESSION_EVENT));
  } catch {
    /* ignore */
  }
}

export function clearPinSession(uid: string): void {
  if (!uid || typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(sessionKey(uid));
  try {
    window.dispatchEvent(new Event(PIN_SESSION_EVENT));
  } catch {
    /* ignore */
  }
}

/** SHA-256 Hex aus 4-stelligem PIN + Mandanten-UID (Salt). */
export async function hashPin(pin: string, salt: string): Promise<string> {
  const data = new TextEncoder().encode(`${pin}${salt}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function isNeverLockPage(pageId: string): boolean {
  return (PIN_LOCK_NEVER_LOCK_PAGES as readonly string[]).includes(pageId);
}

/** Seite gilt als gesperrt (UI-Badge), wenn PIN aktiv, Seite gelistet und Session nicht frei. */
export function isPagePinLockedForUi(
  pageId: string,
  config: PinLockConfig | undefined | null,
  uid: string,
  bypass: boolean
): boolean {
  if (bypass || !uid || isNeverLockPage(pageId)) return false;
  const cfg = normalizePinLockConfig(config);
  if (!cfg.isPinActive || !cfg.pinHash) return false;
  if (!cfg.lockedPageIds.includes(pageId)) return false;
  return !isPinSessionUnlocked(uid);
}
